import {appContext} from "./badge2.context.js";
import {LoggerService} from "./domain/logger.service.js";
import {PlaceholderService} from "./domain/placeholder.service.js";
import {BadgeService} from "./domain/badge.service.js";
import {UrlService} from "./domain/url.service.js";
import { defaultLanguage, fallbackLanguage, I18nService } from './domain/i18n.service.js';
import {ResponsivenessService} from "./domain/responsiveness.service.js";
import {MetadataService} from "./domain/metadata.service.js";
import { GlobalService } from './domain/global.service.js';
import { loadContext } from './badge2.load-context.js';
import { DomDetector } from './utils/dom-detector.js';

export const badge2Boot = async () => {
  // before loading badges, make sure DOMContentLoaded has been emitted
  await ResponsivenessService.whenPageLoaded();

  // prepares DI context with required services/objects
  loadContext();

  const globalService = /** @type GlobalService */ appContext.obtain(GlobalService);
  const logger = /** @type LoggerService */ appContext.obtain(LoggerService);
  const urlService = /** @type UrlService */ appContext.obtain(UrlService);
  const i18nService = /** @type I18nService */ appContext.obtain(I18nService);
  const rootDomDetector = /** @type DomDetector */ appContext.obtain(DomDetector);
  const metadataService = /** @type MetadataService */ appContext.obtain(MetadataService);
  const placeholderService = /** @type PlaceholderService */ appContext.obtain(PlaceholderService);
  const badgeService = /** @type BadgeService */ appContext.obtain(BadgeService);
  const responsivenessService = /** @type ResponsivenessService */ appContext.obtain(ResponsivenessService);

  logger.info('global/booting', 'Booting badge2');

  // boot only on compatible browsers
  if (!ResponsivenessService.isBrowserCompatible()) {
    logger.error('global/missing-capability/unsupported-browser', 'Badge2 not compatible with this browser');
    return;
  }

  // gives some global access through window, e.g. for debugging purposes
  globalService.init();

  // determine urls - e.g. for static assets and api calls
  urlService.init();

  // default and fallback languages should always be available
  await i18nService.loadLanguage(defaultLanguage);
  await i18nService.loadLanguage(fallbackLanguage);

  // initialize responsiveness / e.g detect initial platform
  responsivenessService.initialize();

  if (globalService.externalControl) {
    // when external control is enabled, we will not use any metadata or placeholders no read any configuration
    // instead, the external controller must manually send config data dn trigger rendering of the appropriate badges
    logger.info('external-control/enabled', 'External control has been enabled.');
  } else {
    // handle badge placeholders when they are detected
    placeholderService.onPlaceholderFound(placeholder => badgeService.checkPlaceholder(placeholder));
    placeholderService.onPlaceholderRemoved(placeholder => badgeService.deactivateBadgeFor(placeholder));

    // make sure root dom detector runs
    rootDomDetector.activate();

    // start looking for metadata
    metadataService.start();
  }

  // platform sync
  responsivenessService.onPlatformChange(platform => badgeService.changePlatform(platform));
};
